import { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import { Loader2, X } from 'lucide-react';
import jsPDF from 'jspdf';
import domtoimage from 'dom-to-image';
import { userService } from "../../_services";
import CardFront from "../Summary/CardFront";
import CardBack from "../Summary/CardBack";
import AppleWallet from "../../images/apple-wallet.svg";
import GoogleWallet from "../../images/google-wallet.svg";
import AppleModal from "./AppleModal";
import OrderPage from "../Admin/Users/OrderPage";
import Resources from "../../resources.json";
import shareCardBg from "../../images/share-card-bg.png";

/**
 * ShareCard Page - Complete dev-v3 layout with Shadcn
 * 4 sections: Download Card, Add to Wallet, Order Cards, Share Card
 */
function ShareCard() {
    // User & Group State
    const [groupNumbers, setGroupNumbers] = useState([]);
    const [selectedGroup, setSelectedGroup] = useState('');
    const [groupData, setGroupData] = useState(null);
    const [shortname, setShortname] = useState('');
    const [fullName, setFullName] = useState('');
    
    // Device Detection
    const [deviceName, setDeviceName] = useState('');
    const [browserName, setBrowserName] = useState('');
    
    // Wallet State
    const [applePopup, setApplePopup] = useState(false);
    const [showApplePopup, setShowApplePopup] = useState(false);
    
    // Email Sharing State
    const [emailList, setEmailList] = useState([]);
    const [emailField, setEmailField] = useState('');
    const [errors, setErrors] = useState('');
    const [errorEmail, setErrorEmail] = useState('');
    const [loader, setLoader] = useState(false);
    
    // Order Popup State
    const [showOrderPopup, setShowOrderPopup] = useState(false);
    
    // Loading State
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        try {
            const userAgent = window.navigator.userAgent;
            const devicetype = getUserAgent(userAgent);
            const browsername = getBrowserName(userAgent);
            const Usermeta_Data = window.atob(localStorage.getItem('ajs_user_id'));
            const [userId, userName] = Usermeta_Data.split(':');
            const usernamedata = window.atob(localStorage.getItem('hacker_user_detail'));

            setShortname(usernamedata.split(':')[0]);
            setFullName(usernamedata.split(':')[1]);
            setDeviceName(devicetype);
            setBrowserName(browsername);

            getConsumerGroups(userId, userName);

            const isSafari = /Safari/.test(userAgent) && /Version/.test(userAgent) && !/CriOS/.test(userAgent);
            if (devicetype === 'iPhone' && !isSafari) {
                setShowApplePopup(true);
            }
        } catch (error) {
            console.error('Error initializing:', error);
        }
    }, []);

    const getUserAgent = (userAgent) => {
        if (/iPhone|iPad|iPod/i.test(userAgent)) return 'iPhone';
        if (/Android/i.test(userAgent)) return 'Android';
        return 'Desktop';
    };

    const getBrowserName = (userAgent) => {
        if (/Firefox/i.test(userAgent)) return 'Firefox';
        if (/Chrome/i.test(userAgent)) return 'Chrome';
        if (/Safari/i.test(userAgent)) return 'Safari';
        if (/MSIE|Trident/i.test(userAgent)) return 'Internet Explorer';
        if (/Edge/i.test(userAgent)) return 'Edge';
        if (/Opera|OPR/i.test(userAgent)) return 'Opera';
        if (/Brave/i.test(userAgent)) return 'Brave';
        if (/Vivaldi/i.test(userAgent)) return 'Vivaldi';
        if (/Yandex/i.test(userAgent)) return 'Yandex';
        if (/Chromium/i.test(userAgent)) return 'Chromium';
        return 'Unknown';
    };

    const getConsumerGroups = async (userId, userName) => {
        try {
            const apidata = await userService.GetConsumerGroups(userId, userName);

            if (apidata && apidata.length > 0) {
                setGroupNumbers(apidata);
                setSelectedGroup(apidata[0].groupnumber);
                setGroupData(apidata[0]);
                setLoading(false);

                // Generate passes for first group
                appleGooglePassClick('apple', apidata[0].groupnumber);
                appleGooglePassClick('google', apidata[0].groupnumber);
            }
        } catch (error) {
            console.error('Error getting consumer groups:', error);
            setLoading(false);
        }
    };

    const updateGroup = (e) => {
        const groupNo = e.target.value;
        const sgd = groupNumbers.find(item => item.groupnumber === groupNo);
        if (sgd) {
            setSelectedGroup(groupNo);
            setGroupData(sgd);
            appleGooglePassClick('apple', groupNo);
            appleGooglePassClick('google', groupNo);
        }
    };

    const printCard = () => {
        window.print();
        window.location.reload();
    };

    const downloadCard = (cardId, name) => {
        domtoimage.toPng(document.getElementById(cardId))
            .then(function (blob) {
                const doc = new jsPDF("p", "mm", "a4");
                const pdfWidth = doc.internal.pageSize.getWidth();
                const pdfHeight = doc.internal.pageSize.getHeight();
                const img = new Image();
                img.src = blob;
                img.onload = function () {
                    const imgWidth = img.width;
                    const imgHeight = img.height;
                    let fixedWidth = pdfWidth - 20;
                    let fixedHeight = (imgHeight / imgWidth) * fixedWidth;

                    if (fixedHeight > pdfHeight - 20) {
                        const scaleFactor = (pdfHeight - 20) / fixedHeight;
                        fixedWidth = fixedWidth * scaleFactor;
                        fixedHeight = fixedHeight * scaleFactor;
                    }

                    doc.addImage(blob, 'JPEG', 10, 10, fixedWidth, fixedHeight);
                    doc.save(name + '.pdf');
                };
            });
    };

    const appleGooglePassClick = async (passtype, groupnum) => {
        try {
            const bin = Resources.ApplicationResources.cards[0].bin;
            const pcn = Resources.ApplicationResources.cards[0].pcn;
            const memberid = Resources.ApplicationResources.cards[0].memberid;
            const appname = Resources.ApplicationResources.pass[0].appname;
            const requestURL = passtype === 'apple' 
                ? Resources.ApplicationResources.pass[0].applelink 
                : Resources.ApplicationResources.pass[0].googlelink;
            
            const walletPass = passtype === 'apple' 
                ? window.localStorage.getItem("applePass")
                : window.localStorage.getItem("googlePass");

            if (walletPass === null || walletPass === '') {
                const apidata = await userService.apple_google_wallet_pass_link(
                    requestURL, 
                    bin, 
                    pcn, 
                    groupnum || selectedGroup, 
                    memberid, 
                    passtype, 
                    appname
                );
                
                if (passtype === 'apple') {
                    window.localStorage.setItem('applePass', apidata);
                } else {
                    window.localStorage.setItem('googlePass', apidata);
                }
            }
        } catch (error) {
            console.error('Error generating pass:', error);
        }
    };

    const appleWalletPopup = () => {
        setApplePopup(!applePopup);
    };

    const openAppleLink = () => {
        window.open(window.localStorage.getItem("applePass"), '_blank');
    };

    const openGoogleLink = () => {
        window.open(window.localStorage.getItem("googlePass"), '_blank');
    };

    // Email Sharing Functions (from dev-v3)
    const handleEmailKeyUp = (e) => {
        try {
            setErrors('');
            setErrorEmail('');
            const email = e.target.value;

            if (email.indexOf(',') > -1) {
                e.target.value = '';
                const inputemail = email.replace(',', '').toLowerCase();

                if (userService.ValidEmailRegex(inputemail) === true) {
                    let existingitem = [...emailList];

                    const isDuplicate = existingitem.some(item => item === inputemail);

                    if (isDuplicate === false) {
                        if (existingitem.length < 10) {
                            existingitem.push(inputemail);
                            setEmailList(existingitem);
                            setEmailField('');
                        } else {
                            setErrorEmail('Max allowed email id is up to 10');
                            setEmailField('');
                            setLoader(false);
                        }
                    } else {
                        setErrorEmail('Email already exits');
                        setEmailField('');
                        setLoader(false);
                    }
                } else {
                    setErrorEmail('Invalid email');
                    setEmailField('');
                    setLoader(false);
                }
            }
        } catch (error) {
            console.error('Error in handleEmailKeyUp:', error);
        }
    };

    const removeEmail = (index) => {
        try {
            setErrors('');
            const updatedList = [...emailList];
            updatedList.splice(index, 1);
            setEmailList(updatedList);
        } catch (error) {
            console.error('Error removing email:', error);
        }
    };

    const shareCard = async () => {
        setLoader(true);
        setErrorEmail('');

        try {
            let emails = [...emailList];
            const fValue = emailField.trim();
            let isCheck = true;

            if (errorEmail.length < 1) {
                if (fValue === '' && emails.length < 1) {
                    setErrorEmail('Email required');
                    setLoader(false);
                    isCheck = false;
                } else if (fValue !== '') {
                    if (userService.ValidEmailRegex(fValue) === false) {
                        setEmailField('');
                        setErrorEmail('Invalid email');
                        setLoader(false);
                        isCheck = false;
                    } else {
                        if (!emails.includes(fValue.toLowerCase())) {
                            emails.push(fValue);
                        }
                    }
                }

                if (emails.length <= 10) {
                    if (emails.length > 0 && isCheck) {
                        const Usermeta_Data = window.atob(localStorage.getItem('ajs_user_id'));
                        const userId = Usermeta_Data.split(':')[0];
                        const userName = Usermeta_Data.split(':')[1];
                        const JsonEmails = JSON.stringify(emails);

                        try {
                            const apidata = await userService.Share_Card(userId, userName, JsonEmails);

                            if (apidata[0].code === "1") {
                                setErrors('Your card is successfully shared');
                                setEmailField('');
                                setEmailList([]);
                                setLoader(false);
                                setTimeout(() => {
                                    setErrors('');
                                }, 2000);
                            } else {
                                setErrors(apidata[0].message);
                                setLoader(false);
                                setTimeout(() => {
                                    setErrors('');
                                }, 2000);
                            }
                        } catch (error) {
                            console.error('Error sharing card:', error);
                            setLoader(false);
                        }
                    }
                } else {
                    setEmailField('');
                    setErrorEmail('Max allowed email id is up to 10');
                    isCheck = false;
                    setLoader(false);
                }
            } else {
                setLoader(false);
            }
        } catch (error) {
            console.error('Error in shareCard:', error);
            setLoader(false);
        }
    };

    const handleEmailFocus = () => {
        setErrors('');
        setErrorEmail('');
    };

    const handleOrderClick = () => {
        setShowOrderPopup(true);
    };

    const toggleOrderPopup = () => {
        setShowOrderPopup(false);
    };

    // Check authentication
    if (userService.Check_Null_Empty(localStorage.getItem("ajs_user_id"))) {
        return <Navigate replace to="/signup" />;
    }

    localStorage.setItem('pagename', 'consumerordercard');

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <Loader2 className="h-12 w-12 animate-spin text-brand-accent" />
            </div>
        );
    }

    const cardId = `card-${selectedGroup}`;
    const cardsData = Resources.ApplicationResources.cards || [];
    const showAppleWallet = deviceName !== 'Android';
    const showGoogleWallet = deviceName !== 'iPhone';

    return (
        <div className="space-y-6">
            {/* Page Header */}
            <div>
                <h1 className="text-3xl font-bold tracking-tight text-foreground">Share Card</h1>
                <p className="text-muted-foreground mt-2">
                    Download, print, or share your prescription savings card
                </p>
            </div>

            {/* 1. Download Card Section */}
            <Card>
                <CardContent className="p-6 space-y-4">
                    <h3 className="text-xl font-semibold">Download Your NuLifeSpan Rx Card</h3>

                    {/* Group Selector */}
                    {groupNumbers.length > 1 && (
                        <div>
                            <label className="block text-sm font-medium mb-2">Select Group Number</label>
                            <select
                                value={selectedGroup}
                                onChange={updateGroup}
                                className="w-full max-w-xs border border-input rounded-md px-3 py-2 bg-background"
                            >
                                {groupNumbers.map((group, index) => (
                                    <option key={index} value={group.groupnumber}>
                                        {group.groupnumber}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    {/* Print/Download Buttons */}
                    <div className="flex gap-2">
                        <Button
                            variant="outline"
                            onClick={printCard}
                        >
                            Print
                        </Button>
                        <Button
                            variant="outline"
                            onClick={() => downloadCard(cardId, `${cardsData[0]?.pcn || 'Card'}_${selectedGroup}`)}
                        >
                            Download
                        </Button>
                    </div>

                    {/* Card Display - Both Front and Back in Same Container */}
                    {cardsData.map((card, index) => (
                        <div key={index} id={cardId} className="cards space-y-4">
                            <CardFront
                                PrimaryGn={selectedGroup}
                                pcn={card.pcn}
                                bin={card.bin}
                            />
                            <CardBack
                                cardData={card}
                                groupNumber={selectedGroup}
                                mId={Resources.ApplicationResources.cards[0].memberid}
                            />
                        </div>
                    ))}
                </CardContent>
            </Card>

            {/* Grid for Sections 2, 3, 4 */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* 2. Add to Wallet Section */}
                <Card>
                    <CardContent className="p-6">
                        <h3 className="text-lg font-semibold mb-2">Add to wallet</h3>
                        <p className="text-muted-foreground mb-4">
                            It's apple wallet or Google wallet which you can use on the go
                        </p>
                        <div className="flex gap-3 justify-center">
                            {showAppleWallet && (
                                <button
                                    onClick={showApplePopup ? appleWalletPopup : openAppleLink}
                                    className="transition-transform hover:scale-105"
                                >
                                    <img src={AppleWallet} alt="Apple Wallet" className="h-10" />
                                </button>
                            )}
                            {showGoogleWallet && (
                                <button
                                    onClick={openGoogleLink}
                                    className="transition-transform hover:scale-105"
                                >
                                    <img src={GoogleWallet} alt="Google Wallet" className="h-10" />
                                </button>
                            )}
                        </div>
                    </CardContent>
                </Card>

                {/* 3. Order Cards Section */}
                <Card>
                    <CardContent className="p-6">
                        <h3 className="text-lg font-semibold mb-2">Order cards</h3>
                        <p className="text-muted-foreground mb-4">
                            Buy physical printed cards which you can hand out to friends and family
                        </p>
                        <Button variant="secondary" onClick={handleOrderClick}>
                            Order now
                        </Button>
                    </CardContent>
                </Card>

                {/* 4. Share Card Section (Full Width) */}
                <Card className="lg:col-span-2">
                    <CardContent 
                        className="p-6"
                        style={{
                            background: `#fff url(${shareCardBg}) right bottom no-repeat`,
                            backgroundSize: 'auto 100%'
                        }}
                    >
                        <h3 className="text-lg font-semibold mb-2">Share card</h3>
                        <p className="text-base text-muted-foreground mb-4">
                            Enter up to 10 Email ID's separated by a comma.
                        </p>

                        <div className="space-y-4">
                            <div className="flex gap-2 max-w-2xl">
                                <div className="flex-1">
                                    <Input
                                        type="text"
                                        placeholder="Enter your e-mail address"
                                        value={emailField}
                                        onChange={(e) => setEmailField(e.target.value)}
                                        onKeyUp={handleEmailKeyUp}
                                        onFocus={handleEmailFocus}
                                        className={`h-12 ${errorEmail ? 'border-red-500' : ''}`}
                                    />
                                    {errorEmail && (
                                        <p className="text-xs text-red-500 mt-1">{errorEmail}</p>
                                    )}
                                </div>

                                <Button
                                    onClick={shareCard}
                                    disabled={loader}
                                    variant="secondary"
                                    className="h-12"
                                >
                                    {loader ? (
                                        <><Loader2 className="h-4 w-4 animate-spin mr-2" />Sharing...</>
                                    ) : (
                                        'Share'
                                    )}
                                </Button>
                            </div>

                            {emailList.length > 0 && (
                                <div className="flex flex-wrap gap-2">
                                    {emailList.map((email, index) => (
                                        <Badge key={index} variant="secondary" className="gap-1 pr-1">
                                            {email}
                                            <button
                                                onClick={() => removeEmail(index)}
                                                className="ml-1 hover:bg-black/20 rounded-full p-0.5"
                                            >
                                                <X className="h-3 w-3" />
                                            </button>
                                        </Badge>
                                    ))}
                                </div>
                            )}

                            {errors && (
                                <Alert variant={errors.includes('successfully') ? 'default' : 'destructive'}>
                                    <AlertDescription>{errors}</AlertDescription>
                                </Alert>
                            )}
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Modals */}
            {applePopup && (
                <AppleModal closeModal={appleWalletPopup} />
            )}

            {showOrderPopup && (
                <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
                    <div className="bg-background rounded-lg max-w-4xl w-full max-h-[90vh] overflow-auto m-4">
                        <OrderPage 
                            toggle_user_detail={toggleOrderPopup} 
                            forCard={true} 
                            groupNumbers={groupNumbers} 
                        />
                    </div>
                </div>
            )}
        </div>
    );
}

export default ShareCard;
